// Kostaja industries data. First-class structured schema.
// Real Estate is treated as a major customer path with its own page,
// NOT buried inside Professional Services.
//
// Schema:
// {
//   slug, name, summary,
//   heroHeadline, heroSub,              // industry-specific hero copy
//   commonProblems[], buyerCares[],     // what we fix, what buyers need to feel
//   startingPath[],                     // numbered text steps
//   recommendedServices[],              // service slugs in display order
//   recommendedShopResources[],         // shop resource slugs
//   bestNeeds[],                        // need slugs
//   tone,                               // free-form context (not displayed)
// }

export const INDUSTRIES = [
  // -------------------- REAL ESTATE (first-class, prominent) --------------------
  {
    slug: "real-estate",
    name: "Real Estate",
    summary:
      "Look like the kind of agent, team, or brokerage buyers and sellers actually want handling their biggest transaction, before the first call.",
    heroHeadline:
      "Trust before the showing. Trust before the call.",
    heroSub:
      "Real estate buyers and sellers pick their agent in seconds. A sharper brand, a website that shows the work, and a local presence they can find make you the safer choice before anyone dials the number.",
    commonProblems: [
      "Weak personal brand that does not match the price points you serve",
      "Outdated website that does not present listings, services, or proof clearly",
      "Inconsistent listing presentation across platforms",
      "Scattered social content with no recognisable voice",
      "Low-trust first impression on cold inquiries",
      "Generic agent materials that look like every other listing",
      "Unclear development or property messaging that loses serious buyers",
      "Weak follow-up materials after a showing or pitch",
      "No clear booking or inquiry path for buyer and seller leads",
    ],
    buyerCares: [
      "Does this agent or brokerage look like they handle this price point",
      "Can I figure out their process in under a minute",
      "Do the listings and materials feel handled or rushed",
      "Are reviews, proof, and credibility easy to see",
      "Is it obvious how to book a showing or start a conversation",
    ],
    startingPath: [
      "Start with Brand and Website Review if you already have a public presence and want to know what to fix first.",
      "Start with Brand Foundation if the identity feels generic or inconsistent with the work you do.",
      "Start with Website Foundation or Growth Build if the main issue is the website not selling the service.",
      "Add Social Profile Kit, Business Card, Postcard, or One-Page Sales Sheet for stronger client-facing materials.",
      "Add Google Business Profile Setup or Optimization when local discovery and trust signals matter.",
      "Book a Strategy Call when the right starting point is unclear.",
    ],
    recommendedServices: [
      "brand-and-website-review",
      "brand-foundation-core",
      "website-growth-build",
      "social-profile-kit",
      "one-page-sales-sheet",
      "google-business-profile-optimization",
      "local-visibility-starter-kit",
      "pitch-deck-refinement",
      "postcard-design",
      "business-card-design",
      "review-request-card-design",
      "qr-code-card-design",
    ],
    recommendedShopResources: [
      "website-readiness-checklist",
      "brand-audit-checklist",
      "service-menu-builder",
      "social-content-planner",
      "website-copy-planner",
      "review-request-template-pack",
      "review-response-template-pack",
      "google-business-profile-growth-toolkit",
    ],
    bestNeeds: ["look-more-credible", "more-local-customers", "rebrand-and-relaunch"],
    bestServices: [
      "brand-and-website-review",
      "brand-foundation-core",
      "website-growth-build",
      "google-business-profile-optimization",
      "social-profile-kit",
    ],
    tone: "real-estate",
  },

  // -------------------- CONSTRUCTION --------------------
  {
    slug: "construction",
    name: "Construction",
    summary:
      "Win larger jobs, attract stronger crews, and get the quote signed by looking as serious as the work you actually deliver.",
    heroHeadline:
      "Win the quote. Win the crew. Win the next call.",
    heroSub:
      "Construction buyers hand over serious money. A polished brand, a website that shows real projects, and clean sales materials make signing the quote feel safe, and make the company easy to refer to the next job.",
    commonProblems: [
      "Brand that looks smaller than the work you actually deliver",
      "Website that does not show project value or process",
      "No clear path from inquiry to quote",
      "Weak recruitment presence when hiring skilled labour",
      "Inconsistent print and sales materials at the meeting",
      "No professional proof for higher-ticket clients",
    ],
    buyerCares: [
      "Can this company handle a project at my size",
      "Is the quoting process organised and trustworthy",
      "Will they show up, communicate, and finish",
      "Does the brand feel as professional as the build",
    ],
    startingPath: [
      "Start with Brand and Website Review if the foundation already exists but needs sharpening.",
      "Start with Brand Foundation Core if the identity is weak or generic.",
      "Move to Website Growth Build to present projects, process, and proof.",
      "Add One-Page Sales Sheet and Pitch Deck Refinement for larger bids.",
      "Add Google Business Profile Optimization for stronger local credibility.",
      "Book a Strategy Call to plan the right sequence.",
    ],
    recommendedServices: [
      "brand-and-website-review",
      "brand-foundation-core",
      "website-growth-build",
      "one-page-sales-sheet",
      "pitch-deck-refinement",
      "google-business-profile-optimization",
      "business-card-design",
    ],
    recommendedShopResources: [
      "website-readiness-checklist",
      "brand-audit-checklist",
      "service-menu-builder",
      "review-request-template-pack",
    ],
    bestNeeds: ["look-more-credible", "scaling-up", "more-local-customers"],
    bestServices: [
      "brand-foundation-core",
      "website-growth-build",
      "pitch-deck-refinement",
      "google-business-profile-optimization",
      "one-page-sales-sheet",
    ],
    tone: "construction",
  },

  // -------------------- HOME SERVICES --------------------
  {
    slug: "home-services",
    name: "Home Services",
    summary:
      "Become the first name locals see, trust, and book, without buying ads for every empty slot in the calendar.",
    heroHeadline: "Be the local choice. On the map and in the inbox.",
    heroSub:
      "Home services customers compare fast. A real Google profile, a website that answers the real questions, and a steady review pipeline turn searches into booked jobs before a competitor even shows up.",
    commonProblems: [
      "Profile that is not showing in the local map results",
      "Website that does not explain each service clearly",
      "Inconsistent reviews and no review pipeline",
      "No clear booking or inquiry path",
      "Weak follow-up assets after a quote",
    ],
    buyerCares: [
      "Will this business actually show up",
      "Are reviews recent and real",
      "Is the price range obvious enough to inquire",
      "Can I book or call in under a minute",
    ],
    startingPath: [
      "Start with Local SEO Snapshot or Brand and Website Review.",
      "Move to Website Foundation or Growth Build to present services clearly.",
      "Optimize the Google Business Profile.",
      "Add Local Visibility Starter Kit to handle in-person and follow-up touchpoints.",
      "Subscribe to monthly Google Business Profile Growth Support for consistency.",
    ],
    recommendedServices: [
      "local-seo-snapshot",
      "website-foundation-starter",
      "website-growth-build",
      "google-business-profile-setup",
      "google-business-profile-optimization",
      "local-visibility-starter-kit",
      "google-business-profile-growth-support",
    ],
    recommendedShopResources: [
      "review-request-template-pack",
      "review-response-template-pack",
      "google-business-profile-growth-toolkit",
      "service-menu-builder",
    ],
    bestNeeds: ["more-local-customers", "website-doesnt-convert"],
    bestServices: [
      "google-business-profile-optimization",
      "website-foundation-starter",
      "local-visibility-starter-kit",
      "google-business-profile-growth-support",
      "brand-foundation-starter",
    ],
    tone: "home-services",
  },

  // -------------------- RESTAURANTS AND BARS --------------------
  {
    slug: "restaurants-and-bars",
    name: "Restaurants and Bars",
    summary:
      "Fill the room from search results and keep them coming back from the brand, without discounting your way there.",
    heroHeadline: "Walk-ins from the search bar. Repeat visits from the brand.",
    heroSub:
      "Guests decide with their eyes and their gut. A menu that reads well, a Google profile that stays alive, and a brand that feels current turn scrolls and searches into full tables.",
    commonProblems: [
      "Cluttered or inconsistent menu",
      "Profile photos that do not match the experience",
      "Posts and offers that stop and start",
      "Reviews not being asked for or responded to",
      "Weak social presence that drifts off-brand",
    ],
    buyerCares: [
      "Does this place feel inviting and current",
      "Is the menu and price level clear",
      "Are the photos honest",
      "Are reviews recent and well-handled",
    ],
    startingPath: [
      "Start with Brand Audit or Brand and Website Review.",
      "Refresh the brand with Brand Foundation Core if needed.",
      "Refine the menu with Menu Design.",
      "Optimize the Google Business Profile.",
      "Set a monthly cadence with Google Business Profile Growth Support.",
    ],
    recommendedServices: [
      "brand-and-website-review",
      "brand-foundation-core",
      "menu-design",
      "google-business-profile-optimization",
      "google-business-profile-growth-support-plus",
      "website-growth-build",
      "social-profile-kit",
    ],
    recommendedShopResources: [
      "service-menu-builder",
      "social-content-planner",
      "google-business-profile-growth-toolkit",
    ],
    bestNeeds: ["more-local-customers", "look-more-credible"],
    bestServices: [
      "brand-foundation-core",
      "menu-design",
      "google-business-profile-optimization",
      "website-growth-build",
      "social-profile-kit",
    ],
    tone: "hospitality",
  },

  // -------------------- HEALTH AND WELLNESS --------------------
  {
    slug: "health-and-wellness",
    name: "Health and Wellness",
    summary:
      "Feel safe, current, and easy to book, so patients and clients pick your practice with confidence and keep coming back.",
    heroHeadline: "Confidence before the consultation.",
    heroSub:
      "People trust their body and their calm to your practice. A brand that feels safe, a website that lets them book without a phone call, and a service menu written in plain English do that job before you meet them.",
    commonProblems: [
      "Website that does not feel as professional as the practice",
      "Unclear or cluttered service menu",
      "Weak booking or intake path",
      "Inconsistent location signals across the web",
      "Stale photos and posts",
    ],
    buyerCares: [
      "Is this practice safe, current, and well-run",
      "Can I book without phoning",
      "Are services and pricing clearly explained",
      "Are reviews and credentials easy to find",
    ],
    startingPath: [
      "Start with Brand and Website Review or Brand Audit.",
      "Refresh identity with Brand Foundation Core.",
      "Move to Website Growth Build with a strong service menu.",
      "Optimize the Google Business Profile.",
      "Add Social Profile Kit and Marketing Support Retainer Starter for consistency.",
    ],
    recommendedServices: [
      "brand-and-website-review",
      "brand-foundation-core",
      "website-growth-build",
      "google-business-profile-optimization",
      "social-profile-kit",
      "marketing-support-retainer-starter",
    ],
    recommendedShopResources: [
      "service-menu-builder",
      "website-copy-planner",
      "review-request-template-pack",
    ],
    bestNeeds: ["look-more-credible", "more-local-customers", "website-doesnt-convert"],
    bestServices: [
      "brand-foundation-core",
      "website-growth-build",
      "google-business-profile-optimization",
      "social-profile-kit",
      "marketing-support-retainer-starter",
    ],
    tone: "health",
  },

  // -------------------- FINANCIAL SERVICES --------------------
  {
    slug: "financial-services",
    name: "Financial Services",
    summary:
      "Earn the second meeting by looking as calm, clear, and capable as the advice actually is.",
    heroHeadline: "Clarity that earns the second meeting.",
    heroSub:
      "Financial buyers need to feel respected before they feel sold to. A brand that reads senior, a website that explains the work without jargon, and materials that hold up at the meeting table earn the return call.",
    commonProblems: [
      "Website that feels generic or compliance-flat",
      "Unclear service explanation that loses serious prospects",
      "Weak sales materials at the meeting table",
      "No clear booking path for consultations",
    ],
    buyerCares: [
      "Does this firm understand my situation",
      "Is the process explained without jargon",
      "Is the team easy to reach and easy to refer",
    ],
    startingPath: [
      "Start with Brand and Website Review or Brand Audit.",
      "Refresh identity with Brand Foundation Core or Complete.",
      "Move to Website Growth Build with clear service explanations.",
      "Add Pitch Deck Refinement and One-Page Sales Sheet for client conversations.",
    ],
    recommendedServices: [
      "brand-and-website-review",
      "brand-foundation-core",
      "brand-foundation-complete",
      "website-growth-build",
      "pitch-deck-refinement",
      "one-page-sales-sheet",
    ],
    recommendedShopResources: ["website-copy-planner", "service-menu-builder"],
    bestNeeds: ["look-more-credible", "scaling-up"],
    bestServices: [
      "brand-foundation-core",
      "website-growth-build",
      "pitch-deck-refinement",
      "one-page-sales-sheet",
      "brand-foundation-complete",
    ],
    tone: "financial",
  },

  // -------------------- LEGAL SERVICES --------------------
  {
    slug: "legal-services",
    name: "Legal Services",
    summary:
      "Read as serious counsel in the first ten seconds, so the right cases pick up the phone instead of shopping around.",
    heroHeadline: "Read as serious in the first ten seconds.",
    heroSub:
      "Legal buyers scan for competence and calm. Practice-area pages that answer the real questions, a brand that reads senior, and a clear consultation path turn quiet inbound into booked calls.",
    commonProblems: [
      "Site that does not present practice areas clearly",
      "Generic identity that hides the firm's seniority",
      "Weak consultation path",
      "Inconsistent local discovery",
    ],
    buyerCares: [
      "Does this firm handle my situation",
      "How do I book a consultation",
      "Is the practice clearly senior",
    ],
    startingPath: [
      "Start with Brand and Website Review.",
      "Refresh identity with Brand Foundation Core.",
      "Move to Website Growth Build with strong practice-area pages.",
      "Optimize the Google Business Profile.",
    ],
    recommendedServices: [
      "brand-and-website-review",
      "brand-foundation-core",
      "website-growth-build",
      "google-business-profile-optimization",
      "one-page-sales-sheet",
    ],
    recommendedShopResources: ["website-copy-planner", "service-menu-builder"],
    bestNeeds: ["look-more-credible", "rebrand-and-relaunch"],
    bestServices: [
      "brand-foundation-core",
      "website-growth-build",
      "google-business-profile-optimization",
      "one-page-sales-sheet",
      "brand-and-website-growth-system",
    ],
    tone: "legal",
  },

  // -------------------- RETAIL --------------------
  {
    slug: "retail",
    name: "Retail",
    summary:
      "Feel like the same store on the shelf, in the bag, and on the feed, so customers come back on purpose and bring their friends.",
    heroHeadline: "Pick up the brand. Pick up the bag.",
    heroSub:
      "Retail is a repeat-visit game. When packaging, signage, social, and the storefront finally look like one brand, customers stop trying you once and start telling their friends.",
    commonProblems: [
      "Inconsistent brand from shelf to social",
      "Signage and offers that drift off-brand",
      "Weak local search presence",
      "No clean way to share the latest drop or offer",
    ],
    buyerCares: [
      "Does this store feel curated",
      "Are the latest offers easy to see",
      "Is it easy to find and visit",
    ],
    startingPath: [
      "Start with Brand Audit or Brand and Website Review.",
      "Refresh identity with Brand Foundation Core.",
      "Add Website Growth Build to present the catalog or signature offer.",
      "Add Social Profile Kit, Sticker Design, Postcard Design.",
      "Optimize the Google Business Profile.",
    ],
    recommendedServices: [
      "brand-foundation-core",
      "website-growth-build",
      "social-profile-kit",
      "sticker-design",
      "postcard-design",
      "google-business-profile-optimization",
    ],
    recommendedShopResources: [
      "social-content-planner",
      "review-request-template-pack",
      "google-business-profile-growth-toolkit",
    ],
    bestNeeds: ["look-more-credible", "more-local-customers"],
    bestServices: [
      "brand-foundation-core",
      "website-growth-build",
      "social-profile-kit",
      "sticker-design",
      "postcard-design",
    ],
    tone: "retail",
  },

  // -------------------- HOSPITALITY --------------------
  {
    slug: "hospitality",
    name: "Hospitality",
    summary:
      "Feel the welcome before the visit, so guests book instead of comparing another five listings on the same tab.",
    heroHeadline: "Feel the welcome before the visit.",
    heroSub:
      "Hospitality guests choose with their eyes and their gut. A brand that matches the experience, a website that makes booking easy, and honest photos turn browsers into confirmed reservations.",
    commonProblems: [
      "Website that does not match the venue",
      "Inconsistent visual story across channels",
      "Weak local profile coverage",
      "Stale offers and events",
    ],
    buyerCares: [
      "Is this somewhere I want to spend money",
      "Can I book or hold a date easily",
      "Are the photos and reviews honest",
    ],
    startingPath: [
      "Start with Brand and Website Review.",
      "Refresh identity with Brand Foundation Core.",
      "Add Website Growth Build with strong booking paths.",
      "Optimize the Google Business Profile.",
      "Subscribe to Google Business Profile Growth Support Plus for ongoing presence.",
    ],
    recommendedServices: [
      "brand-and-website-review",
      "brand-foundation-core",
      "website-growth-build",
      "menu-design",
      "google-business-profile-optimization",
      "google-business-profile-growth-support-plus",
    ],
    recommendedShopResources: ["social-content-planner", "service-menu-builder"],
    bestNeeds: ["look-more-credible", "more-local-customers"],
    bestServices: [
      "brand-foundation-core",
      "website-growth-build",
      "menu-design",
      "google-business-profile-optimization",
      "google-business-profile-growth-support-plus",
    ],
    tone: "hospitality",
  },

  // -------------------- PROFESSIONAL SERVICES --------------------
  {
    slug: "professional-services",
    name: "Professional Services",
    summary:
      "Get chosen on the first visit, not the fifth touchpoint, by looking as senior as the work actually is.",
    heroHeadline: "Trust, explanation, and authority on the first visit.",
    heroSub:
      "Professional services buyers decide on clarity. A brand that matches the seniority, a website that explains the engagement, and sales materials that hold up on-screen and in-room turn interest into signed work.",
    commonProblems: [
      "Generic brand that does not match the seniority of the work",
      "Service explanation that is too long or too vague",
      "Weak sales materials in proposals and decks",
      "No clear path to a consultation",
    ],
    buyerCares: [
      "Do they handle work like mine",
      "How does the engagement actually work",
      "Is the team senior enough for my situation",
    ],
    startingPath: [
      "Start with Brand and Website Review.",
      "Refresh identity with Brand Foundation Core.",
      "Move to Website Growth Build.",
      "Add Pitch Deck Refinement and One-Page Sales Sheet.",
      "Subscribe to Marketing Support Retainer Starter for consistent campaigns.",
    ],
    recommendedServices: [
      "brand-and-website-review",
      "brand-foundation-core",
      "website-growth-build",
      "pitch-deck-refinement",
      "one-page-sales-sheet",
      "marketing-support-retainer-starter",
    ],
    recommendedShopResources: ["website-copy-planner", "service-menu-builder", "brand-audit-checklist"],
    bestNeeds: ["look-more-credible", "website-doesnt-convert", "scaling-up"],
    bestServices: [
      "brand-foundation-core",
      "website-growth-build",
      "pitch-deck-refinement",
      "brand-and-website-growth-system",
      "marketing-support-retainer-starter",
    ],
    tone: "pro-services",
  },

  // -------------------- LOCAL SERVICES (umbrella for trades) --------------------
  {
    slug: "local-services",
    name: "Local Services",
    summary:
      "Be the obvious local choice on the map, on the phone, and at the door, without begging for referrals.",
    heroHeadline: "Be the obvious local choice.",
    heroSub:
      "Local buyers compare in minutes. A brand that reads professional, a website that shows the service area, and a Google profile that stays alive make you the first call, and the easy referral.",
    commonProblems: [
      "Weak Google Business Profile",
      "Website that does not present services or area",
      "Inconsistent brand at the storefront and online",
      "No review pipeline",
    ],
    buyerCares: ["Is this nearby and trustworthy", "Can I book now", "Are reviews recent"],
    startingPath: [
      "Start with Local SEO Snapshot or Brand and Website Review.",
      "Brand Foundation Starter or Core if identity is weak.",
      "Website Foundation Starter to present services and area.",
      "Google Business Profile Setup or Optimization.",
      "Local Visibility Starter Kit for ongoing visibility.",
    ],
    recommendedServices: [
      "local-seo-snapshot",
      "brand-foundation-starter",
      "website-foundation-starter",
      "google-business-profile-setup",
      "local-visibility-starter-kit",
      "local-business-launch-kit",
    ],
    recommendedShopResources: [
      "google-business-profile-growth-toolkit",
      "review-request-template-pack",
      "service-menu-builder",
    ],
    bestNeeds: ["more-local-customers", "look-more-credible", "starting-a-new-business"],
    bestServices: [
      "brand-foundation-starter",
      "website-foundation-starter",
      "google-business-profile-setup",
      "local-business-launch-kit",
      "local-visibility-starter-kit",
    ],
    tone: "local-services",
  },

  // -------------------- NONPROFITS --------------------
  {
    slug: "nonprofits",
    name: "Nonprofits and Community",
    summary:
      "Feel as serious as the mission, so donors, partners, and volunteers say yes faster and stay longer.",
    heroHeadline: "A mission that feels as serious as the work behind it.",
    heroSub:
      "Donors and partners want to see the mission handled. A brand that reads current, a website that shows the programs clearly, and campaign materials that carry the same voice turn interest into contributions.",
    commonProblems: [
      "Brand that feels older than the mission",
      "Website that does not present programs or campaigns clearly",
      "Weak donation or sponsorship path",
      "Inconsistent campaign assets",
    ],
    buyerCares: [
      "Is this organization credible",
      "Where does the money go",
      "How do I get involved",
    ],
    startingPath: [
      "Start with Brand and Website Review.",
      "Refresh identity with Brand Foundation Core.",
      "Move to Website Growth Build with strong programs pages.",
      "Add One-Page Sales Sheet and Postcard Design for campaigns.",
    ],
    recommendedServices: [
      "brand-and-website-review",
      "brand-foundation-core",
      "website-growth-build",
      "one-page-sales-sheet",
      "postcard-design",
    ],
    recommendedShopResources: ["website-copy-planner", "social-content-planner"],
    bestNeeds: ["look-more-credible", "rebrand-and-relaunch"],
    bestServices: [
      "brand-foundation-core",
      "website-growth-build",
      "one-page-sales-sheet",
      "postcard-design",
    ],
    tone: "nonprofit",
  },

  // -------------------- STARTUPS --------------------
  {
    slug: "startups",
    name: "Startups",
    summary:
      "Look ready before investors, partners, or customers ask, so the deck earns the meeting and the meeting earns the yes.",
    heroHeadline: "Look ready before you are asked.",
    heroSub:
      "Early-stage buyers and investors decide on signals. A brand that reads real, a landing page that converts, and a deck that hides nothing turn cold outreach into warm conversations.",
    commonProblems: [
      "Brand that screams early-stage in a bad way",
      "Deck that hides the value",
      "Landing page that does not convert traffic",
      "Inconsistent identity across touchpoints",
    ],
    buyerCares: [
      "Is this team serious",
      "Is the offer clear",
      "Can I see what this becomes",
    ],
    startingPath: [
      "Start with Brand Foundation Core.",
      "Add Website Growth Build or Website Foundation Starter.",
      "Add Pitch Deck Refinement.",
      "Optimize Social Profile Kit and Digital Product Launch Support if relevant.",
    ],
    recommendedServices: [
      "brand-foundation-core",
      "brand-foundation-complete",
      "website-growth-build",
      "pitch-deck-refinement",
      "digital-product-launch-support",
      "social-profile-kit",
    ],
    recommendedShopResources: ["website-copy-planner", "brand-audit-checklist"],
    bestNeeds: ["starting-a-new-business", "look-more-credible"],
    bestServices: [
      "brand-foundation-core",
      "website-growth-build",
      "pitch-deck-refinement",
      "digital-product-launch-support",
      "social-profile-kit",
    ],
    tone: "startup",
  },

  // -------------------- TECH AND SAAS --------------------
  {
    slug: "tech-and-saas",
    name: "Tech and SaaS",
    summary:
      "Look like the company your product roadmap deserves, so the sales cycle stops stalling at the brand.",
    heroHeadline: "Look like the company your roadmap deserves.",
    heroSub:
      "Tech buyers compare quickly. A brand that reads confident, a site that puts the offer above the fold, and a deck that stands up next to funded competitors make your product the safer bet in the shortlist.",
    commonProblems: [
      "Brand that does not match the technical depth",
      "Site that buries the offer",
      "Deck that does not stand up next to competitors",
    ],
    buyerCares: ["Is this product credible", "How does this help my team", "Who is behind it"],
    startingPath: [
      "Start with Brand Foundation Complete.",
      "Move to Website Growth Build or Website System Complete.",
      "Add Pitch Deck Refinement.",
      "Add Digital Product Launch Support if relevant.",
    ],
    recommendedServices: [
      "brand-foundation-complete",
      "website-growth-build",
      "website-system-complete",
      "pitch-deck-refinement",
      "digital-product-launch-support",
    ],
    recommendedShopResources: ["website-copy-planner", "brand-audit-checklist"],
    bestNeeds: ["scaling-up", "rebrand-and-relaunch", "look-more-credible"],
    bestServices: [
      "brand-foundation-complete",
      "website-growth-build",
      "website-system-complete",
      "pitch-deck-refinement",
      "digital-product-launch-support",
    ],
    tone: "tech",
  },

  // -------------------- CREATORS AND PERSONAL BRANDS --------------------
  {
    slug: "creators-and-coaches",
    name: "Creators and Personal Brands",
    summary:
      "Turn an existing audience into paying customers with a brand, offer, and product presentation that finally match the work.",
    heroHeadline: "A personal brand that earns the audience you already have.",
    heroSub:
      "Creators win on positioning. A brand that reads professional, an offer that fits on one line, and product covers that look sold-out-ready turn followers into buyers.",
    commonProblems: [
      "Inconsistent brand across channels",
      "Offer that is hard to explain in one line",
      "Digital products that look like drafts",
    ],
    buyerCares: [
      "Is this person legit",
      "Is the offer clear",
      "Do I want what they sell",
    ],
    startingPath: [
      "Start with Brand Foundation Starter or Core.",
      "Add Social Profile Kit.",
      "Add Digital Product Launch Support and Cover Design when ready to ship.",
      "Add Canva Brand Presence Kit for ongoing in-platform consistency.",
    ],
    recommendedServices: [
      "brand-foundation-starter",
      "brand-foundation-core",
      "social-profile-kit",
      "digital-product-launch-support",
      "digital-product-cover-design",
      "canva-brand-presence-kit-plus",
    ],
    recommendedShopResources: ["social-content-planner", "website-copy-planner"],
    bestNeeds: ["look-more-credible", "starting-a-new-business"],
    bestServices: [
      "brand-foundation-starter",
      "digital-product-launch-support",
      "digital-product-cover-design",
      "canva-brand-presence-kit-plus",
      "social-profile-kit",
    ],
    tone: "creators",
  },
];

export const getIndustryBySlug = (slug) => INDUSTRIES.find((i) => i.slug === slug);
